import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';
import { CHAT_TABLE } from '../Chat/chatConstants';
import { getClientId } from '../../utils/clientIdentity';

const { FiCalendar, FiPlus, FiTrash2, FiClock, FiSend } = FiIcons;

export default function UpcomingEventsManager() {
  const [events, setEvents] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [bannerUrl, setBannerUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [notifying, setNotifying] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    const { data } = await supabase
      .from('gallo_events_1786733905424')
      .select('*')
      .order('scheduled_at', { ascending: true });
    if (data) setEvents(data);
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!title.trim() || !scheduledAt) return;
    setLoading(true);

    const { error } = await supabase.from('gallo_events_1786733905424').insert({
      title: title.trim(),
      description: description.trim(),
      scheduled_at: new Date(scheduledAt).toISOString(),
      banner_url: bannerUrl.trim() || 'https://images.unsplash.com/photo-1549417229-aa67d3263c09?auto=format&fit=crop&w=600&q=80'
    });

    if (!error) {
      setTitle('');
      setDescription('');
      setScheduledAt('');
      setBannerUrl('');
      fetchEvents();
    }
    setLoading(false);
  };

  const handleBroadcastReminder = async (eventItem) => {
    setNotifying(true);
    try {
      await supabase.from(CHAT_TABLE).insert({
        nickname: '📢 RECORDATORIO DE EVENTO',
        content: `¡No te lo pierdas! "${eventItem.title}" programado para el ${new Date(eventItem.scheduled_at).toLocaleString()}.`,
        sender_id: getClientId(),
        is_private: false
      });
      alert('Recordatorio emitido al chat general de espectadores.');
    } catch (e) {
      console.error(e);
    } finally {
      setNotifying(false);
    }
  };

  const handleDeleteEvent = async (id) => {
    if (window.confirm('¿Seguro que deseas eliminar este evento programado?')) {
      await supabase.from('gallo_events_1786733905424').delete().eq('id', id);
      fetchEvents();
    }
  };

  return (
    <section className="rounded-2xl border border-white/10 bg-[#111] p-5 space-y-6">
      <div>
        <h3 className="font-semibold text-white text-sm sm:text-base flex items-center gap-2">
          <SafeIcon icon={FiCalendar} className="text-red-500" /> Próximos Eventos y Recordatorios
        </h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Programa los próximos encuentros en vivo. Los espectadores podrán ver una cuenta regresiva interactiva y guardarlo en su calendario.
        </p>
      </div>

      <form onSubmit={handleCreateEvent} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5">Título del Evento</label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej. Gran Derby de Otoño"
              className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-xs text-white focus:border-red-600 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5">Fecha y Hora</label>
            <input
              type="datetime-local"
              required
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-xs text-white focus:border-red-600 focus:outline-none"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5">Descripción Breve</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ej. Con los criaderos de mayor renombre."
              className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-xs text-white focus:border-red-600 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5">URL de Banner</label>
            <input
              type="url"
              value={bannerUrl}
              onChange={(e) => setBannerUrl(e.target.value)}
              placeholder="https://images.unsplash.com/..."
              className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-xs text-white focus:border-red-600 focus:outline-none"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || !title.trim() || !scheduledAt}
          className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-xs sm:text-sm transition-all"
        >
          <span className="flex items-center justify-center gap-1.5">
            <SafeIcon icon={FiPlus} size={14} /> Programar Evento
          </span>
        </button>
      </form>

      <div className="border-t border-white/5 pt-5 space-y-3">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Eventos Programados & Recordatorios</h4>
        <div className="space-y-3 max-h-[300px] overflow-y-auto">
          {events.map((ev) => (
            <div key={ev.id} className="flex items-center justify-between p-3 bg-black/40 border border-white/5 rounded-xl gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <img
                  src={ev.banner_url || 'https://images.unsplash.com/photo-1549417229-aa67d3263c09?auto=format&fit=crop&w=100&q=80'}
                  alt=""
                  className="w-12 h-12 object-cover rounded-lg shrink-0 border border-white/10"
                />
                <div className="min-w-0">
                  <p className="text-xs font-bold text-white truncate">{ev.title}</p>
                  <p className="text-[10px] text-gray-400 truncate">{ev.description || 'Sin descripción'}</p>
                  <p className="text-[9px] text-red-400 font-semibold flex items-center gap-1 mt-0.5">
                    <SafeIcon icon={FiClock} size={10} /> {new Date(ev.scheduled_at).toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  disabled={notifying}
                  onClick={() => handleBroadcastReminder(ev)}
                  title="Enviar recordatorio general al chat"
                  className="px-2.5 py-1.5 rounded-lg bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 text-xs font-semibold flex items-center gap-1 transition-all"
                >
                  <SafeIcon icon={FiSend} size={12} /> Avisar
                </button>
                <button
                  onClick={() => handleDeleteEvent(ev.id)}
                  className="p-1.5 text-gray-500 hover:text-red-500 hover:bg-white/5 rounded transition-all"
                >
                  <SafeIcon icon={FiTrash2} size={14} />
                </button>
              </div>
            </div>
          ))}
          {events.length === 0 && (
            <p className="text-center text-xs text-gray-600 py-4">No hay eventos programados.</p>
          )}
        </div>
      </div>
    </section>
  );
}